import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { catchError, EMPTY, finalize, throwError } from "rxjs";
import {
  NotificationService,
  VISITT_MAINTENANCE_UNAVAILABLE_COOLDOWN_MS,
  VISITT_MAINTENANCE_UNAVAILABLE_THROTTLE_KEY,
} from "./notifications.service";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { AuthTokenService } from "./auth/auth-token.service";
import { LoadingService } from "./loading.service";
import { ALLOW_FORBIDDEN_RESPONSE, ALLOW_UNAUTHORIZED_RESPONSE, LOGIN_INVALID_DETAILS_MESSAGE, SUPPRESS_GLOBAL_LOADER, SUPPRESS_GLOBAL_SERVER_ERROR_TOAST } from "./http-context-tokens";
import { getServerErrorText, guestCreateOpenPathConflictMessage, isGuestCreateOpenPathConflictError, isVisittMaintenanceSearchUpstreamFailure, toHebrewSsoAuthMessage } from "./sso-auth-error-message";

const LOADER_DELAY_BEFORE_LOGIN_MS = 2000;
const LOADER_DELAY_AFTER_LOGIN_MS = 5000;

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const notify = inject(NotificationService);
  const router = inject(Router);
  const authToken = inject(AuthTokenService);
  const loading = inject(LoadingService);

  const hasSession = !!authToken.getToken();
  const loaderDelayMs = hasSession
    ? LOADER_DELAY_AFTER_LOGIN_MS
    : LOADER_DELAY_BEFORE_LOGIN_MS;

  const suppressLoader = req.context.get(SUPPRESS_GLOBAL_LOADER) === true;

  let loaderShown = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (!suppressLoader) {
    timeoutId = setTimeout(() => {
      loaderShown = true;
      loading.show();
    }, loaderDelayMs);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isUnauthorized = error?.status === 401;

      // The API returns 403 (body: { status: "Forbidden", errorNumber: 403 }) whenever the caller's
      // session is no longer valid for the resource — there is no "benign" 403 in this app, so any
      // 403 is treated like a 401: force a fresh login.
      const isForbidden = error?.status === 403;

      // A 403 with the roles-changed marker means the token's permissions no longer match SSO
      // (a role was added/removed) — same forced-logout outcome, but with a specific message.
      const isRolesChanged =
        isForbidden &&
        (error.headers?.get('X-Auth-Roles-Changed') === 'true' ||
          (error.error as { status?: string } | null)?.status === 'RolesChanged');

      if (isForbidden && !isRolesChanged && req.context.get(ALLOW_FORBIDDEN_RESPONSE) === true) {
        return throwError(() => error);
      }

      if (isUnauthorized && req.context.get(ALLOW_UNAUTHORIZED_RESPONSE) === true) {
        return throwError(() => error);
      }

      if (isUnauthorized || isForbidden) {
        authToken.clearSession();

        if (isRolesChanged) {
          notify.showError('ההרשאות שלך עודכנו. יש להתחבר מחדש.');
        }

        // Avoid navigation loops if the error is coming from the login flow itself.
        const isAlreadyOnLogin =
          typeof router.url === 'string' && router.url.startsWith('/auth/login');

        if (!isAlreadyOnLogin) {
          router.navigateByUrl('/auth/login', {
            replaceUrl: true,
            state: { returnUrl: router.url }
          });
        }
        return EMPTY;
      }

      if (isVisittMaintenanceSearchUpstreamFailure(error)) {
        if (req.context.get(SUPPRESS_GLOBAL_SERVER_ERROR_TOAST) === true) {
          return throwError(() => error);
        }
        notify.showErrorThrottled(
          'שירות קריאות השירות אינו זמין כרגע. ניתן להמשיך לגלוש ולנסות שוב בעוד מספר דקות.',
          VISITT_MAINTENANCE_UNAVAILABLE_THROTTLE_KEY,
          VISITT_MAINTENANCE_UNAVAILABLE_COOLDOWN_MS,
        );
        return EMPTY;
      }

      const isGuestCreatePost =
        req.method === 'POST' && /\/guests(\?.*)?$/.test(req.url.split('?')[0]);
      if (isGuestCreatePost && isGuestCreateOpenPathConflictError(error)) {
        if (req.context.get(SUPPRESS_GLOBAL_SERVER_ERROR_TOAST) === true) {
          return throwError(() => error);
        }
        notify.showError(guestCreateOpenPathConflictMessage());
        return EMPTY;
      }

      const isLoginInvalidDetailsOverride =
        error?.status === 500 &&
        req.context.get(LOGIN_INVALID_DETAILS_MESSAGE) === true;

      if (isLoginInvalidDetailsOverride) {
        notify.showError('הפרטים שהוזנו אינם נכונים');
        return EMPTY;
      }

      const isLoginFlow = req.context.get(LOGIN_INVALID_DETAILS_MESSAGE) === true;
      if (isLoginFlow && error?.status === 400) {
        const serverText = getServerErrorText(error);
        notify.showError(toHebrewSsoAuthMessage(serverText));
        return EMPTY;
      }

      if (req.context.get(SUPPRESS_GLOBAL_SERVER_ERROR_TOAST) === true) {
        return throwError(() => error);
      }

      let errorMessage = '';
      // Check if ErrorEvent is defined (it is not on the server)
      const isErrorEventDefined = typeof ErrorEvent !== 'undefined';
      if (isErrorEventDefined && error.error instanceof ErrorEvent) {        
        // Client-side error
        errorMessage = `Client error: ${error.error.message}`;
      } else {
        // Server-side error        
        const serverText = getServerErrorText(error);
        errorMessage = serverText ?? "אירעה שגיאת מערכת, נא לפנות למנהל המערכת";
        notify.showError(errorMessage);
      }
      console.error(errorMessage);
      return EMPTY;
    }),
    finalize(() => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      if (loaderShown) loading.hide();
    })
  );
};

